import React, { useState, useCallback } from 'react';
import { DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  createNotificationEmailPreferences,
} from '../../../../api/diagnose_dashboard/notifications';
import { Alert, Button, Col, Flex, message, Modal, Row, Select, Space, Switch, Table, Tag, theme, Tooltip, Typography } from 'antd';
import { alertTypeColumns, dataSource } from '../helpers';

const { useToken } = theme;

const frequencyOptions = [
    { value: 24, label: 'Daily' },
    { value: 168, label: 'Weekly' },
    { value: 720, label: 'Monthly' },
]
const { Text } = Typography;

function EmailPreferencesModal({
  alertTypes,
  currentUser,
  onClose,
  showEmailPreference,
  propertyList,
  savedUserPreferences,
  setSavedUserPreferences
}) {
  const { email = '', accountId = '' } = currentUser || {};
  const [first_name = '', last_name = ''] = (currentUser?.name || '').split(' ');
  const [isLoading, setLoading] = useState(false);
  const [isInfoModalVisible, setInfoModalVisible] = useState(false);
  const [showDelete, setShowDelete] = useState(null);
  const [all, ...initialNotificationTypes] = alertTypes;
  const [rowErrors, setRowErrors] = useState({});
  const [globalValidationError, setGlobalValidationError] = useState('');
  const propertOptions = propertyList?.map((option, idx) => ({ key: `${option.id}-${idx}`, value: option.id, label: option.display_name }));
  const [tempEmailPreferences, setTempEmailPreferences] = useState(savedUserPreferences)
  const { token } = useToken();

  const clearRowError = useCallback((index) => {
    setRowErrors((prev) => {
      const updated = { ...prev };
      if (updated[index]) {
        delete updated[index];
      }
      return updated;
    });
    setGlobalValidationError('');
  }, []);

  const updateUserPreference = useCallback((index, updatedFields) => {
    setTempEmailPreferences((prev) => prev.map((item, idx) => (idx === index ? { ...item, ...updatedFields } : item)));
  }, []);

  const handleAddPreferences = useCallback(() => {
    const newRow = {
      email,
      first_name,
      last_name,
      emailing_enabled: false,
      frequency: 24,
      preferences: [],
      websites: [],
      initialStatus: false,
    };
    setTempEmailPreferences((prev) => [...prev, newRow]);
  }, [email, first_name, last_name]);

  const handleToggleEmailPreferences = useCallback(
    (index, val) => {
      updateUserPreference(index, { emailing_enabled: val });
    },
    [updateUserPreference],
  );

  const handlePreferences = useCallback(
    (index, val) => {
      updateUserPreference(index, { preferences: val });
      clearRowError(index);
    },
    [updateUserPreference, clearRowError],
  );

  const handleProperty = useCallback(
    (index, val) => {
      const selectedWebsites = propertyList.filter((prop) => val.includes(prop.id));
      updateUserPreference(index, { websites: selectedWebsites });
      clearRowError(index);
    },
    [updateUserPreference, clearRowError, propertyList],
  );

  const handleFrequency = useCallback(
    (index, val) => {
      updateUserPreference(index, { frequency: val });
      clearRowError(index);
    },
    [updateUserPreference, clearRowError],
  );

  const validatePreferences = useCallback((preferencesList = [], alertTypes = []) => {
    const errors = {};
    let globalErrorMsg = '';
    const existingCombos = {};
    const duplicateWebsites = new Set();
    const duplicateTypes = new Set();

    preferencesList.forEach((row, index) => {
      const rowErrors = {};
      const websites = row.websites || [];
      const preferences = row.preferences || [];

      // Required field checks
      if (websites.length === 0) {
        rowErrors.property = 'Select a property';
      }
      if (preferences.length === 0) {
        rowErrors.preferences = 'Select a alert type';
      }

      // Only check for duplicates if both fields have values
      if (websites.length > 0 && preferences.length > 0) {
        websites.forEach((site) => {
          preferences.forEach((alertType) => {
            const key = `${site.id}__${alertType}`;

            if (existingCombos[key]) {
              const existing = existingCombos[key];

              if (existing.frequency !== row.frequency) {
                // Conflict due to different frequency
                rowErrors.property = 'Select different property';
                rowErrors.preferences = 'Select different alert type';
                globalErrorMsg = 'Please select different property and/or alert type';
              } else {
                // Exact match already exists
                duplicateWebsites.add(site.display_name);
                duplicateTypes.add(alertType);
                rowErrors.property = 'Select different property';
                rowErrors.preferences = 'Select different alert type';
              }
            } else {
              existingCombos[key] = { frequency: row.frequency, index };
            }
          });
        });
      }

      if (Object.keys(rowErrors).length > 0) {
        errors[index] = rowErrors;
      }
    });

    // Generate global error message for duplicates
    if (!globalErrorMsg && duplicateWebsites.size > 0 && duplicateTypes.size > 0) {
      const alertTypeNames = [...duplicateTypes].map((type) => alertTypes.find((n) => n.value === type)?.label || type);
      globalErrorMsg = (
        <Text>
          You already have a preference set up for <Text italic>{alertTypeNames.join(', ')}</Text> alert for{' '}
          {[...duplicateWebsites].join(', ')}
        </Text>
      );
    }

    return { errors, globalErrorMsg };
  }, []);

  const saveEmailPreferences = useCallback(async () => {
    try {
      const { errors, globalErrorMsg } = validatePreferences(tempEmailPreferences, alertTypes);

      if (Object.keys(errors).length > 0) {
        setRowErrors(errors);
        setGlobalValidationError(globalErrorMsg);
        return;
      }

      setRowErrors({});
      setGlobalValidationError('');
      setLoading(true);

      const users = tempEmailPreferences.map(({ id, initialStatus, ...rest }) => ({
        ...rest,
        preferences: rest.preferences || [],
        websites: rest.websites || [],
      }));

      const res = await createNotificationEmailPreferences(users, accountId);

      if (res) {
        message.success('Email preferences saved successfully!');
        onClose();
        setTempEmailPreferences(users);
        setSavedUserPreferences(users);
      } else {
        message.error('Error while saving email preferences.');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      message.error('Unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }, [tempEmailPreferences, alertTypes, accountId]);

  const handleDeletePreferences = useCallback(async () => {
    if (!showDelete) return;

    try {
      setLoading(true);

      const updatedUsers = tempEmailPreferences
        .filter((_, index) => index !== showDelete)
        .map(({ id, initialStatus, ...rest }) => ({
          ...rest,
          preferences: rest.preferences || [],
          websites: rest.websites || [],
        }));

      setTempEmailPreferences(updatedUsers);
      setShowDelete(null);
      setRowErrors({});
      setGlobalValidationError('');
    } catch (error) {
      console.error('Error deleting preferences:', error);
      message.error('Unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }, [showDelete, tempEmailPreferences]);

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text, record, index) => {
        return index === 0 ? <div>{text}</div> : <div />;
      },
      width: 187,
    },
    {
      title: 'Property',
      width: 199,
      render: (text, record, index) => {
        return (
          <>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Select Property"
              value={record.websites.map((w) => w.id)}
              onChange={(val) => handleProperty(index, val)}
              tagRender={Tag}
              showSearch
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
              maxTagCount={1}
              allowClear
              options={propertOptions}
            />
            {!!rowErrors[index]?.property && (
              <div style={{ color: 'red', marginTop: 4 }}>{rowErrors[index].property}</div>
            )}
          </>
        );
      },
    },
    {
      title: () => {
        return (
          <Flex align="center" justify="space-between">
            <span>Alert Types</span>
            <Tooltip title="Click for more alert types">
              <Button
                onClick={() => setInfoModalVisible(true)}
                type="text"
                style={{ color: token.colorTextDisabled }}
                icon={<InfoCircleOutlined size={'small'} />}
              ></Button>
            </Tooltip>
          </Flex>
        );
      },
      dataIndex: 'type',
      key: 'type',
      render: (text, record, index) => {
        return (
          <>
            <Select
              mode="multiple"
              placeholder="Select Alert Type"
              style={{ width: '100%' }}
              value={
                initialNotificationTypes.every((pref) => record.preferences.includes(pref.value))
                  ? alertTypes.map((opt) => opt.value)
                  : record.preferences
              }
              onChange={(val) => handlePreferences(index, val)}
              maxTagCount={1}
              allowClear
              tagRender={Tag}
              showSearch={false}
              options={alertTypes}
            />
            {!!rowErrors[index]?.preferences && (
              <div style={{ color: 'red', marginTop: 4 }}>{rowErrors[index].preferences}</div>
            )}
          </>
        );
      },
      width: 199,
    },
    {
      title: 'Alert Frequency',
      width: 199,
      render: (text, record, index) => {
        return (
          <>
            <Select
              placeholder="Select Frequency"
              style={{ width: '100%' }}
              value={record?.frequency}
              onChange={(val) => handleFrequency(index, val)}
              tagRender={Tag}
              maxTagCount={1}
              allowClear
              showSearch={false}
              options={frequencyOptions}
            />
          </>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'emailing_enabled',
      key: 'emailing_enabled',
      render: (text, record, index) => (
        <>
          <Flex align="center" justify="space-between">
            <Flex gap={token.marginSM} align="center">
              <Switch
                id={'notification-list-toggle'}
                checked={text}
                title={text ? 'Running' : 'Paused'}
                onChange={(val) => handleToggleEmailPreferences(index, val)}
              />
              <Text style={{ color: token.colorLink }}>{text ? 'Running' : 'Paused'}</Text>
            </Flex>
            <div>
              {index === 0 ? null : (
                <Tooltip title="Delete" placement="top">
                  <Button
                    type="text"
                    style={{ color: token.colorPrimary }}
                    icon={<DeleteOutlined />}
                    onClick={() => setShowDelete(index)}
                  ></Button>
                </Tooltip>
              )}
            </div>
          </Flex>
        </>
      ),
      width: 190,
    },
  ];

  return (
    <>
      <Modal
        title="Email Preferences"
        open={showEmailPreference}
        onCancel={onClose}
        okButtonProps={{
          disabled: isLoading || Object.keys(rowErrors).length > 0,
          loading: isLoading,
          onClick: saveEmailPreferences,
        }}
        cancelButtonProps={{ type: "link", onClick: onClose}}
        okText={"Save Preferences"}
        destroyOnClose
        width={1086}
      >
        <Row gutter={[0, token.margin]}>
          <Col span={24}>
            <Text type="secondary">
              You can select all the properties and alerts that you would like to get notified of via email and also
              choose the frequency of the emails. At any point, you can choose to pause the emails by disabling the
              running toggle.
            </Text>
          </Col>
          <Col span={24}>
            <Alert
              type="info"
              message="You can add up to 5 email preferences. After that, adding more new preferences won’t be available."
              showIcon
              closable
            />
          </Col>
          <Col span={24}>
            <Table
              style={{ marginTop: token.margin }}
              dataSource={tempEmailPreferences}
              bordered={false}
              rowKey={(record) => record?.email}
              columns={columns}
              pagination={false}
              showSorterTooltip={false}
              loading={isLoading}
            />
          </Col>
          <Col span={24}>
            <Flex flex={[1, 0]} justify="flex-end" align="center">
              {globalValidationError && (
                <Alert
                  type="error"
                  showIcon
                  closable
                  message={globalValidationError}
                  onClose={() => setGlobalValidationError('')}
                  style={{ width: '100%' }}
                />
              )}
              {tempEmailPreferences.length >= 5 ? null : (
                <Space style={{ padding: `0 ${token.padding}px 0 ${token.padding}px` }}>
                  <Button type="default" onClick={handleAddPreferences}>
                    Add new preference
                  </Button>
                </Space>
              )}
            </Flex>
          </Col>
          {showDelete ? (
            <Col span={24}>
              <Alert
                style={{ marginTop: token.margin }}
                message="Are you sure you want to delete ‘Email Preference’?"
                description="You won’t receive any email alerts for the selected properties and alerts."
                type="warning"
                action={
                  <Flex align="center" gap={token.marginXS} style={{ marginTop: token.margin }}>
                    <Button type="link" onClick={() => setShowDelete(false)}>
                      Cancel
                    </Button>
                    <Button type="primary" onClick={handleDeletePreferences}>
                      Delete
                    </Button>
                  </Flex>
                }
                showIcon
              />
            </Col>
          ) : null}
        </Row>
      </Modal>
      <Modal
        open={isInfoModalVisible}
        title="All Alerts"
        onCancel={() => setInfoModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setInfoModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        <Table dataSource={dataSource} columns={alertTypeColumns} pagination={false} bordered size="small" />
      </Modal>
    </>
  );
}

export default EmailPreferencesModal;
